import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Search } from 'lucide-react';
import { categories } from '../../utils/calculators';

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [calcDropdown, setCalcDropdown] = useState(false);
  const [marketsDropdown, setMarketsDropdown] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hoverState, setHoverState] = useState<string | null>(null);
  const navigate = useNavigate();

  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdowns on route change
  useEffect(() => {
    setCalcDropdown(false);
    setMarketsDropdown(false);
    setMobileOpen(false);
    setSearchOpen(false);
  }, [navigate]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // In a real app, navigate to search results
      // navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const navLinkStyle = ({ isActive }: { isActive: boolean }) => ({
    padding: '8px 16px',
    color: isActive ? 'var(--accent)' : 'var(--text-primary)',
    textDecoration: 'none',
    fontWeight: isActive ? '700' : '600',
    fontSize: '18px',
    borderRadius: '4px',
    transition: 'color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'Outfit, sans-serif'
  });

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '0',
    backgroundColor: '#ffffff',
    border: '1px solid #E2E8F0',
    borderRadius: '12px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
    padding: '16px',
    marginTop: '0',
    top: 'calc(100% + 8px)',
    zIndex: 100
  };

  // Invisible bridge to prevent hover loss
  const bridgeStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '0',
    width: '100%',
    height: '8px',
    backgroundColor: 'transparent',
    zIndex: 99
  };

  return (
    <header style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #E2E8F0', position: 'relative', zIndex: 50 }}>
      <div className="max-w-6xl mx-auto px-4 flex items-center justify-between h-20">
        
        {/* Left: Logo & Navigation */}
        <div className="flex items-center gap-8">
          <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
            <img src="/logos/stock-marketor-logo-header.webp" alt="StockMarketor" width="311" height="48" style={{ height: '48px', width: 'auto' }} />
          </Link>

          <nav className="hidden md:flex items-center gap-2">
            
            {/* Calculators Mega Menu */}
            <div className="relative" onMouseEnter={() => setCalcDropdown(true)} onMouseLeave={() => setCalcDropdown(false)}>
              <button style={{ ...navLinkStyle({ isActive: false }), background: 'none', border: 'none', cursor: 'pointer' }}>
                Calculators <ChevronDown size={16} style={{ transform: calcDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              
              {calcDropdown && (
                <div style={{
                  position: 'absolute', top: '100%', left: '-100px', 
                  backgroundColor: '#ffffff', border: '1px solid #E2E8F0',
                  borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                  width: '900px', padding: '32px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '40px'
                }}>
                  {/* Column 1 */}
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Stock Average & Savings</h4>
                      <div className="flex flex-col gap-2">
                        {categories.find(c => c.id === 'stock-average')?.calculators.slice(0, 3).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                        {categories.find(c => c.id === 'savings')?.calculators.slice(0, 3).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Technical Analysis</h4>
                      <div className="flex flex-col gap-2">
                        {categories.find(c => c.id === 'technical')?.calculators.slice(0, 3).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Column 2 */}
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Returns & Options</h4>
                      <div className="flex flex-col gap-2">
                        {categories.find(c => c.id === 'returns')?.calculators.slice(0, 4).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                        {categories.find(c => c.id === 'options')?.calculators.slice(0, 3).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Column 3 */}
                  <div>
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Valuation & Risk</h4>
                      <div className="flex flex-col gap-2">
                        {categories.find(c => c.id === 'valuation')?.calculators.slice(0, 4).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                        {categories.find(c => c.id === 'risk')?.calculators.slice(0, 4).map(c => (
                          <Link key={c.path} to={c.path} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', transition: 'all 0.2s' }}>{c.name}</Link>
                        ))}
                      </div>
                    </div>
                    <Link to="/calculators" style={{ display: 'inline-block', marginTop: '8px', color: '#0052FF', fontWeight: 'bold', textDecoration: 'none', fontSize: '16px' }}>View all calculators →</Link>
                  </div>

                </div>
              )}
            </div>

            {/* Markets Mega Menu */}
            <div className="relative" onMouseEnter={() => setMarketsDropdown(true)} onMouseLeave={() => setMarketsDropdown(false)}>
              <button style={{ ...navLinkStyle({ isActive: false }), background: 'none', border: 'none', cursor: 'pointer' }}>
                Markets <ChevronDown size={16} style={{ transform: marketsDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              
              {marketsDropdown && (
                <div style={{
                  position: 'absolute', top: '100%', left: '-200px', 
                  backgroundColor: '#ffffff', border: '1px solid #E2E8F0',
                  borderRadius: '12px', boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                  width: '900px', padding: '32px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '24px'
                }}>
                  {/* Col 1 */}
                  <div>
                    <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>North America</h4>
                    <div className="flex flex-col gap-1">
                      <Link to="/calculators/us" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '600', padding: '6px 8px', borderRadius: '4px' }}>🇺🇸 US Market Tools</Link>
                      <Link to="/markets/usa" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#4A5568', textDecoration: 'none', fontSize: '15px', padding: '6px 8px', borderRadius: '4px', marginLeft: '24px' }}>Stock Return Calc</Link>
                      <Link to="/markets/canada" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', marginTop: '8px' }}>🇨🇦 Canada</Link>
                      <Link to="/markets/mexico" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px' }}>🇲🇽 Mexico</Link>
                    </div>
                  </div>
                  
                  {/* Col 2 */}
                  <div>
                    <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Europe</h4>
                    <div className="flex flex-col gap-1">
                      {['UK', 'Germany', 'France', 'Italy', 'Spain', 'Netherlands', 'Switzerland', 'Sweden'].map(country => (
                        <Link key={country} to={`/markets/${country.toLowerCase()}`} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px' }}>{country}</Link>
                      ))}
                    </div>
                  </div>

                  {/* Col 3 */}
                  <div>
                    <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Asia & Oceania</h4>
                    <div className="flex flex-col gap-1">
                      <Link to="/calculators/india" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '600', padding: '6px 8px', borderRadius: '4px' }}>🇮🇳 India Market Tools</Link>
                      <Link to="/markets/india" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#4A5568', textDecoration: 'none', fontSize: '15px', padding: '6px 8px', borderRadius: '4px', marginLeft: '24px' }}>Stock Return Calc</Link>
                      {['Japan', 'China', 'Hong-Kong', 'Australia', 'Singapore', 'Taiwan'].map(country => (
                        <Link key={country} to={`/markets/${country.toLowerCase()}`} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px' }}>
                          {country.replace('-', ' ')}
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Col 4 */}
                  <div>
                    <h4 style={{ fontSize: '15px', fontWeight: '800', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '8px' }}>Global & Forex</h4>
                    <div className="flex flex-col gap-1">
                      <Link to="/calculators/international" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '600', padding: '6px 8px', borderRadius: '4px' }}>🌐 All International</Link>
                      <Link to="/calculators/forex" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '600', padding: '6px 8px', borderRadius: '4px', marginTop: '12px' }}>💱 Forex & Currency</Link>
                      
                      <div style={{ marginTop: '16px' }}>
                        {['Brazil', 'South-Africa', 'UAE', 'Saudi-Arabia'].map(country => (
                          <Link key={country} to={`/markets/${country.toLowerCase()}`} className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '6px 8px', borderRadius: '4px', display: 'block' }}>
                            {country.replace('-', ' ')}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div 
              className="group relative h-full flex items-center"
              onMouseEnter={() => setHoverState('company')}
              onMouseLeave={() => setHoverState(null)}
              style={{ padding: '20px 0' }} // increase hit area
            >
              <button style={{ ...navLinkStyle({ isActive: false }), background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Company <ChevronDown size={14} style={{ transform: hoverState === 'company' ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }} />
              </button>
              {hoverState === 'company' && (
                <>
                  <div style={bridgeStyle} />
                  <div style={dropdownStyle} className="dropdown-menu">
                    <div className="flex flex-col gap-1 w-48">
                      <Link to="/about" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '10px 16px', borderRadius: '4px' }}>About</Link>
                      <Link to="/blog" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '10px 16px', borderRadius: '4px' }}>Blog</Link>
                      <Link to="/contact" className="hover:text-blue-600 hover:bg-slate-50" style={{ color: '#1A202C', textDecoration: 'none', fontSize: '16px', fontWeight: '500', padding: '10px 16px', borderRadius: '4px' }}>Contact</Link>
                    </div>
                  </div>
                </>
              )}
            </div>
          </nav>
        </div>

        {/* Right: Search & Actions */}
        <div className="flex items-center gap-4">
          
          <div className="relative">
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex items-center bg-slate-100 rounded-full px-3 py-1 border border-slate-200">
                <Search size={18} color="#64748B" />
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder="Search calculators..." 
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{ background: 'none', border: 'none', outline: 'none', padding: '6px 12px', fontSize: '16px', width: '200px', color: '#1A202C', fontFamily: 'Outfit, sans-serif' }}
                  autoFocus
                />
                <button type="button" aria-label="Close search" onClick={() => setSearchOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                  <X size={16} color="#94A3B8" />
                </button>
              </form>
            ) : (
              <button 
                aria-label="Open search"
                onClick={() => setSearchOpen(true)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', color: '#1A202C', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}
                className="hover:bg-slate-100"
              >
                <Search size={22} />
              </button>
            )}
          </div>

          <button
            className="md:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => setMobileOpen(o => !o)}
            style={{ background: 'none', border: 'none', padding: '8px', cursor: 'pointer', color: '#1A202C', display: 'flex', alignItems: 'center' }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div style={{ backgroundColor: '#ffffff', borderTop: '1px solid #E2E8F0', padding: '16px 24px', position: 'absolute', width: '100%', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', zIndex: 100 }} className="md:hidden">
          {[
            { label: 'Home', to: '/' },
            { label: 'All Calculators', to: '/calculators' },
            { label: 'US Market Tools', to: '/calculators/us' },
            { label: 'India Market Tools', to: '/calculators/india' },
            { label: 'International Markets', to: '/calculators/international' },
            { label: 'Forex & Currency', to: '/calculators/forex' },
            { label: 'About', to: '/about' },
            { label: 'Blog', to: '/blog' },
            { label: 'Contact', to: '/contact' },
          ].map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMobileOpen(false)}
              style={({ isActive }) => ({
                display: 'block',
                padding: '12px 0',
                color: isActive ? '#0052FF' : '#1A202C',
                textDecoration: 'none',
                fontSize: '18px',
                borderBottom: '1px solid #E2E8F0',
                fontWeight: isActive ? '700' : '500',
                fontFamily: 'Outfit, sans-serif'
              })}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}
